import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from "recharts";

const PALETTE = ["#8C583E", "#5C7C5A", "#E97B1B", "#A6896A",
                 "#D72121", "#1F54E0", "#7B4FA8", "#3A8D89"];

const mmss = (s) => s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : "—";

export default function RoastOverlayModal({ ids, onClose }) {
  const [data, setData] = useState(null);
  const [show, setShow] = useState({ bt: true, et: false, ror: false });

  useEffect(() => {
    if (!ids?.length) return;
    api.get("/roast-profiles/curves", { params: { ids: ids.join(",") } })
      .then((r) => setData(r.data))
      .catch(() => setData([]));
  }, [ids]);

  if (!ids?.length) return null;

  // Build merged data: each point becomes
  //   { t, [r{idx}_bt|et|ror|gas|fan|drum]: value }
  const merged = {};
  (data || []).forEach((rp, idx) => {
    (rp.curve || []).forEach((p) => {
      if (!merged[p.t]) merged[p.t] = { t: p.t };
      merged[p.t][`r${idx}_bt`]   = p.bt;
      merged[p.t][`r${idx}_et`]   = p.et;
      merged[p.t][`r${idx}_ror`]  = p.ror;
      merged[p.t][`r${idx}_gas`]  = p.gas;
      merged[p.t][`r${idx}_fan`]  = p.fan;
      merged[p.t][`r${idx}_drum`] = p.drum;
    });
  });
  const series = Object.values(merged).sort((a, b) => a.t - b.t);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-foreground/50 p-0 sm:p-4" data-testid="roast-overlay-modal">
      <div className="bg-card border border-border rounded-t sm:rounded shadow-xl w-full max-w-7xl max-h-[96vh] sm:max-h-[92vh] overflow-y-auto">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 sm:px-6 py-4 border-b border-border sticky top-0 bg-card z-10">
          <div>
            <p className="label-tiny">Comparison</p>
            <h3 className="font-display text-lg sm:text-xl font-bold">Roast curve overlay · {ids.length} roasts</h3>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <ToggleChip label="BT"  active={show.bt}  onClick={() => setShow((s) => ({ ...s, bt: !s.bt }))} />
            <ToggleChip label="ET"  active={show.et}  onClick={() => setShow((s) => ({ ...s, et: !s.et }))} />
            <ToggleChip label="RoR" active={show.ror} onClick={() => setShow((s) => ({ ...s, ror: !s.ror }))} />
            <button onClick={onClose} data-testid="overlay-close" className="p-2 hover:bg-secondary rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        {!data && <p className="p-6 text-muted-foreground">Loading curves…</p>}
        {data && data.length === 0 && <p className="p-6 text-muted-foreground">No curve data for the selected roasts.</p>}
        {data && data.length > 0 && (
          <div className="p-4 sm:p-6">
            {/* Legend cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {data.map((rp, idx) => (
                <div key={rp.id} className="bg-card border rounded p-3" style={{ borderLeftWidth: 4, borderLeftColor: PALETTE[idx % PALETTE.length] }}>
                  <p className="label-tiny mb-1">#{rp.roast_number}</p>
                  <p className="font-semibold text-sm">{rp.origin_name}</p>
                  <p className="text-xs font-mono text-muted-foreground">
                    {rp.start_datetime ? new Date(rp.start_datetime).toLocaleDateString("en-ZA") : "—"}
                    {" · "}{rp.green_weight_kg}→{rp.roasted_weight_kg}kg
                    {rp.loss_pct != null && ` · ${rp.loss_pct.toFixed(1)}% loss`}
                  </p>
                </div>
              ))}
            </div>

            {/* Main BT / ET / RoR comparison */}
            <div className="h-[440px] bg-card border border-border rounded p-3 sm:p-4">
              <ResponsiveContainer>
                <ComposedChart data={series} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(25 10% 65%)" strokeOpacity={0.7} />
                  {/* Explicit horizontal gridlines so they're guaranteed to render
                      with the dual-Y-axis setup */}
                  {[80, 100, 120, 140, 160, 180, 200, 220, 240].map((y) => (
                    <ReferenceLine key={`g-${y}`} yAxisId="temp" y={y}
                      stroke="hsl(25 10% 65%)" strokeOpacity={0.5} strokeDasharray="3 3" />
                  ))}
                  <XAxis dataKey="t" type="number" tickFormatter={mmss} domain={['dataMin', 'dataMax']}
                    ticks={Array.from({ length: 20 }, (_, i) => i * 60)}
                    tick={{ fontSize: 11, fill: "hsl(25 8% 42%)" }} />
                  <YAxis yAxisId="temp" tick={{ fontSize: 11, fill: "hsl(25 8% 42%)" }} domain={[80, 'dataMax + 10']}
                    ticks={[80, 100, 120, 140, 160, 180, 200, 220, 240]}
                    allowDataOverflow
                    label={{ value: "°C", angle: -90, position: "insideLeft", fontSize: 11 }} />
                  <YAxis yAxisId="ror" orientation="right" tick={{ fontSize: 11, fill: "hsl(25 8% 42%)" }}
                    domain={[-4, 20]}
                    ticks={[-4, 0, 4, 8, 12, 16, 20]}
                    allowDataOverflow
                    label={{ value: "RoR", angle: 90, position: "insideRight", fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "white", border: "1px solid hsl(40 21% 87%)", borderRadius: 4, fontSize: 12 }}
                    labelFormatter={(t) => `t = ${mmss(t)}`}
                    formatter={(v, n) => [v != null ? Number(v).toFixed(1) : "—", n]} />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} iconType="plainline" />
                  {data.flatMap((rp, idx) => {
                    const color = PALETTE[idx % PALETTE.length];
                    const tag = `#${rp.roast_number} ${rp.origin_name}`;
                    const lines = [];
                    if (show.bt) {
                      lines.push(
                        <Line key={`${rp.id}-bt`} yAxisId="temp" dataKey={`r${idx}_bt`} name={`${tag} · BT`}
                          stroke={color} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
                      );
                    }
                    if (show.et) {
                      lines.push(
                        <Line key={`${rp.id}-et`} yAxisId="temp" dataKey={`r${idx}_et`} name={`${tag} · ET`}
                          stroke={color} strokeWidth={1.4} strokeDasharray="5 4" dot={false} connectNulls isAnimationActive={false} />
                      );
                    }
                    if (show.ror) {
                      lines.push(
                        <Line key={`${rp.id}-ror`} yAxisId="ror" dataKey={`r${idx}_ror`} name={`${tag} · RoR`}
                          stroke={color} strokeWidth={1.2} strokeDasharray="1 3" dot={false} connectNulls isAnimationActive={false} />
                      );
                    }
                    return lines;
                  })}
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Gas / Fan / Drum comparison strips */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-4">
              <ControlStrip data={data} series={series} field="gas"  label="Gas"  unit="%"  domain={[0, 100]}  />
              <ControlStrip data={data} series={series} field="fan"  label="Fan"  unit="Hz" domain={["dataMin - 1", "dataMax + 1"]} />
              <ControlStrip data={data} series={series} field="drum" label="Drum" unit="Hz" domain={["dataMin - 1", "dataMax + 1"]} />
            </div>

            <p className="text-xs text-muted-foreground mt-3">
              Each colour is one roast. Toggle BT (solid) / ET (dashed) / RoR (dotted, right axis) above. Gas, Fan and Drum strips below show the operator's control inputs for each roast.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function ControlStrip({ data, series, field, label, unit, domain }) {
  return (
    <div className="bg-card border border-border rounded p-3" data-testid={`overlay-strip-${field}`}>
      <p className="label-tiny mb-2">{label} ({unit})</p>
      <div className="h-36">
        <ResponsiveContainer>
          <ComposedChart data={series} margin={{ top: 2, right: 4, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(25 10% 65%)" strokeOpacity={0.6} />
            <XAxis dataKey="t" type="number" tickFormatter={mmss} domain={['dataMin', 'dataMax']}
              tick={{ fontSize: 10, fill: "hsl(25 8% 42%)" }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10, fill: "hsl(25 8% 42%)" }} domain={domain} width={32} />
            <Tooltip contentStyle={{ background: "white", border: "1px solid hsl(40 21% 87%)", borderRadius: 4, fontSize: 11 }}
              labelFormatter={(t) => `t = ${mmss(t)}`}
              formatter={(v, n) => [v != null ? `${Number(v).toFixed(1)} ${unit}` : "—", n]} />
            {data.map((rp, idx) => (
              <Line key={`${rp.id}-${field}`} dataKey={`r${idx}_${field}`} name={`#${rp.roast_number}`}
                stroke={PALETTE[idx % PALETTE.length]} strokeWidth={1.5} dot={false} connectNulls isAnimationActive={false} />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ToggleChip({ label, active, onClick }) {
  return (
    <button onClick={onClick}
      data-testid={`overlay-toggle-${label.toLowerCase()}`}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "bg-card text-muted-foreground border-border hover:bg-secondary"
      }`}>
      {label}
    </button>
  );
}
